import React, { useEffect, useState } from 'react';
import ApiService from '../../service/ApiService';
import { apiPaths } from '../../service/apiPaths';
import { Link, Navigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import Cookies from "js-cookie";

const Orders = () => {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [expandedOrder, setExpandedOrder] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);

    const fetchOrders = async (page = 1) => {
        try {
            setLoading(true);
            const response = await ApiService({
                method: "GET",
                endpoint: `${apiPaths.orderHistory}?page=${page}&limit=10`,
            });
            
            if (response.success) {
                setOrders(response.data.orders || response.data); // Handle both formats
                setPagination(response.data.pagination);
            }
            setLoading(false);
        } catch (error) {
            console.error("Error fetching orders:", error);
            setError("Failed to load orders. Please try again later.");
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchOrders(currentPage);
    }, [currentPage]);

    const formatDate = (dateString, type = "") => {
        const date = new Date(dateString);
        const options = {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        };
        if (type === "long") {
            options.hour = '2-digit';
            options.minute = '2-digit';
            options.timeZone = 'Asia/Kolkata';
            options.timeZoneName = 'short';
        }
        return date.toLocaleDateString('en-IN', options);
    };

    const formatCurrency = (amount) => {
        if (typeof amount === 'object' && amount?.$numberDecimal) {
            return parseFloat(amount.$numberDecimal).toFixed(2);
        }
        return parseFloat(amount || 0).toFixed(2);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            "-1": { label: "Refunded", classes: "bg-amber-50 text-amber-700 border-amber-200" },
            1: { label: "Payment Pending", classes: "bg-blue-50 text-blue-700 border-blue-200" },
            2: { label: "Payment Confirmed", classes: "bg-indigo-50 text-indigo-700 border-indigo-200" },
        };
        const defaultStatus = { label: "Processing", classes: "bg-gray-50 text-gray-700 border-gray-200" };
        return statusMap[status] || defaultStatus;
    };

    const getOrderStatusBadge = (orderStatus) => {
        const orderStatusMap = {
            "-1": { label: "Cancelled", classes: "bg-red-50 text-red-700 border-red-200" },
            0: { label: "Processing", classes: "bg-gray-50 text-gray-700 border-gray-200" },
            1: { label: "Confirmed", classes: "bg-blue-50 text-blue-700 border-blue-200" },
            2: { label: "Dispatched", classes: "bg-indigo-50 text-indigo-700 border-indigo-200" },
            3: { label: "Delivered", classes: "bg-green-50 text-green-700 border-green-200" }
        };
        return orderStatusMap[orderStatus] || { label: "Processing", classes: "bg-gray-50 text-gray-700 border-gray-200" };
    };

    const getOrderStatusSteps = (orderStatus) => {
        const currentStatus = Number(orderStatus) || 0;

        const steps = [
            { name: "Confirmed", status: currentStatus >= 1 ? "complete" : "incomplete" },
            { name: "Dispatched", status: currentStatus >= 2 ? "complete" : "incomplete" },
            { name: "Delivered", status: currentStatus >= 3 ? "complete" : "incomplete" }
        ];

        if (currentStatus === -1) {
            steps.forEach(step => step.status = "incomplete");
            steps.status = "cancelled";
        }

        return steps;
    };

    const OrderStatusStepper = ({ orderStatus }) => {
        const steps = getOrderStatusSteps(orderStatus);
        const isCancelled = orderStatus === -1;

        return (
            <div className="py-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3">Order Status</h3>
                {isCancelled ? (
                    <div className="flex items-center justify-center bg-red-50 py-3 rounded-lg border border-red-200">
                        <span className="text-red-700 font-medium">Order Cancelled</span>
                    </div>
                ) : (
                    <div className="relative">
                        <div className="absolute top-5 left-0 w-full h-1 bg-gray-200" aria-hidden="true"></div>
                        <div className="relative flex justify-between">
                            {steps.map((step, index) => (
                                <div key={index} className="flex flex-col items-center">
                                    <div
                                        className={`w-10 h-10 rounded-full flex items-center justify-center z-10 border-2 ${step.status === "complete"
                                                ? "bg-green-600 border-green-600"
                                                : "bg-white border-gray-300"
                                            }`}
                                    >
                                        {step.status === "complete" ? (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className="text-white">
                                                <polyline points="20 6 9 17 4 12"></polyline>
                                            </svg>
                                        ) : (
                                            <span className="w-4 h-4 rounded-full bg-gray-200"></span>
                                        )}
                                    </div>
                                    <span className={`mt-2 text-xs md:text-sm ${step.status === "complete" ? "text-green-600 font-medium" : "text-gray-500"
                                        }`}>
                                        {step.name}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const toggleOrderExpand = (orderId) => {
        if (expandedOrder === orderId) {
            setExpandedOrder(null);
        } else {
            setExpandedOrder(orderId);
        }
    };

    async function downloadInvoice(orderId) {
        try {
            const token = Cookies.get("accessToken");
            if (!token) {
                return <Navigate to={"/signin"} />
            }

            const response = await fetch(`${process.env.REACT_APP_URL}/order/invoice/${orderId}`, {
                method: "GET",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (!response.ok) {
                throw new Error('Failed to download invoice');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `invoice-${orderId}.pdf`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            window.URL.revokeObjectURL(url);
            toast.success("Invoice download started");
        } catch (error) {
            toast.error(error?.message || "Failed to download invoice");
        }
    }

    // Helper function to get product image from snapshot
    const getProductImage = (product) => {
        // New format - try variant images first, then product images
        if (product.productImages && product.productImages.length > 0) {
            return product.productImages[0];
        }
        if (product.mainImage) {
            return product.mainImage;
        }
        // Fallback for old format
        if (product.product?.productImages) {
            return Array.isArray(product.product.productImages) 
                ? product.product.productImages[0] 
                : product.product.productImages;
        }
        return null;
    };

    // Helper function to get product name
    const getProductName = (product) => {
        // New format
        if (product.name) return product.name;
        // Old format fallback
        return product.product?.productName || product.product?.name || "Unknown Product";
    };

    // Helper function to get product brand
    const getProductBrand = (product) => {
        // New format
        if (product.brand) return product.brand;
        // Old format fallback
        return product.product?.brand || "Unknown Brand";
    };

    // Helper function to get pricing info
    const getProductPricing = (product) => {
        // New format with snapshots
        if (product.originalPrice !== undefined) {
            return {
                originalPrice: product.originalPrice,
                actualPrice: product.actualPaidPrice,
                discountAmount: product.discountAmount,
                itemTotal: product.itemTotal,
                hasDiscount: product.discountAmount > 0
            };
        }
        // Old format fallback
        return {
            originalPrice: product.price || 0,
            actualPrice: product.price || 0,
            discountAmount: 0,
            itemTotal: (product.price || 0) * (product.quantity || 1),
            hasDiscount: false
        };
    };

    if (loading) {
        return (
            <div className="min-h-[60vh] flex items-center justify-center">
                <div className="flex flex-col items-center">
                    <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-t-blue-600 rounded-full animate-spin"></div>
                    <p className="mt-4 text-gray-600">Loading your orders...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-[40vh] flex items-center justify-center px-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-md w-full text-center">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-red-100 rounded-full mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-red-600">
                            <circle cx="12" cy="12" r="10"></circle>
                            <line x1="12" y1="8" x2="12" y2="12"></line>
                            <line x1="12" y1="16" x2="12.01" y2="16"></line>
                        </svg>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Failed to load orders</h3>
                    <p className="text-gray-600">{error}</p>
                    <button
                        onClick={() => fetchOrders(currentPage)}
                        className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                    >
                        Try Again
                    </button>
                </div>
            </div>
        );
    }

    if (orders.length === 0) {
        return (
            <div className="min-h-[50vh] flex flex-col items-center justify-center px-4">
                <div className="bg-white rounded-lg p-8 max-w-md w-full text-center shadow-sm border">
                    <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-50 rounded-full mb-4">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-blue-600">
                            <rect x="8" y="2" width="8" height="4" rx="1" ry="1"></rect>
                            <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"></path>
                            <path d="M12 11h4"></path>
                            <path d="M12 16h4"></path>
                            <path d="M8 11h.01"></path>
                            <path d="M8 16h.01"></path>
                        </svg>
                    </div>
                    <h3 className="text-xl font-medium text-gray-900 mb-2">No orders yet</h3>
                    <p className="text-gray-600 mb-6">Looks like you haven't placed any orders with us yet.</p>
                    <Link
                        to="/shop"
                        className="inline-block px-5 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition"
                    >
                        Browse Products
                    </Link>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="mb-8">
                <h1 className="text-2xl font-bold text-gray-900">My Orders</h1>
                <p className="text-gray-600 mt-1">Track and manage your order history</p>
            </div>

            <div className="space-y-6">
                {orders.map((order) => {
                    const status = getStatusBadge(order.status);
                    const orderStatus = getOrderStatusBadge(order.orderStatus);
                    const isExpanded = expandedOrder === order._id;
                    
                    // Handle both new format (orderTotals) and old format (totalAmount)
                    const totalAmount = order.orderTotals?.finalTotal || order.totalAmount;

                    return (
                        <div
                            key={order._id}
                            className="bg-white border rounded-xl overflow-hidden shadow-sm"
                        >
                            {/* Order Header - Always visible */}
                            <div
                                className="p-4 sm:p-6 border-b cursor-pointer hover:bg-gray-50 transition"
                                onClick={() => toggleOrderExpand(order._id)}
                            >
                                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                                        <div className="flex flex-wrap gap-2">
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${status.classes}`}>
                                                {status.label}
                                            </span>
                                            <span className={`px-3 py-1 text-xs font-medium rounded-full border ${orderStatus.classes}`}>
                                                {orderStatus.label}
                                            </span>
                                        </div>
                                        <div className="text-sm sm:text-base">
                                            <span className="font-medium">
                                                Order #{order.orderNumber || order._id.slice(-6)}
                                            </span>
                                            <span className="text-gray-500 ml-2 hidden sm:inline">•</span>
                                            <span className="text-gray-500 block sm:inline sm:ml-2">
                                                {formatDate(order.createdAt)}
                                            </span>
                                        </div>
                                    </div>
                                    <div className="flex items-center justify-between sm:justify-end gap-4">
                                        <div className="text-right">
                                            <div className="text-xs text-gray-500">Total Amount</div>
                                            <div className="font-semibold">₹{formatCurrency(totalAmount)}</div>
                                        </div>

                                        <div className="transform transition-transform duration-200">
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="20"
                                                height="20"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="2"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className={`text-gray-400 ${isExpanded ? 'rotate-180' : ''}`}
                                            >
                                                <polyline points="6 9 12 15 18 9"></polyline>
                                            </svg>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Order Details - Visible when expanded */}
                            {isExpanded && (
                                <>
                                    {/* Order Status Stepper */}
                                    <div className="bg-white p-4 sm:p-6 border-t">
                                        <OrderStatusStepper orderStatus={order.orderStatus} />
                                    </div>

                                    {/* Order Meta Info */}
                                    <div className="bg-gray-50 p-4 sm:p-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 text-sm">
                                        <div>
                                            <h3 className="font-medium text-gray-900 mb-1">Shipping Address</h3>
                                            <p className="text-gray-600">
                                                {/* New format with shippingAddress */}
                                                {order.shippingAddress ? (
                                                    order.shippingAddress.fullAddress
                                                ) : (
                                                    /* Old format fallback */
                                                    <>
                                                        {order.houseNumber}, {order.societyName}
                                                        {order.street && `, ${order.street}`}
                                                        , {order.city}, {order.state}, {order.pinCode}
                                                    </>
                                                )}
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="font-medium text-gray-900 mb-1">Payment Details</h3>
                                            <p className="text-gray-600">
                                                {order.paymentType === 'cod' ? 'Cash on Delivery' : 'Online Payment'}
                                                {order.paymentId && <span className="block">ID: #{order.paymentId}</span>}
                                            </p>
                                        </div>

                                        <div>
                                            <h3 className="font-medium text-gray-900 mb-1">Additional Info</h3>
                                            <div className="flex flex-col gap-1">
                                                {order.invoiceNumber && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-600">Invoice:</span>
                                                        <span className="font-medium">#{order.invoiceNumber}</span>
                                                    </div>
                                                )}
                                                
                                                {/* Coupon Info - New Format */}
                                                {order.couponApplied && (
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-gray-600">Coupon:</span>
                                                        <span className="font-medium text-green-600">
                                                            {order.couponApplied.couponCode}
                                                        </span>
                                                        <span className="text-xs text-green-600">
                                                            (-₹{formatCurrency(order.couponApplied.discountAmount)})
                                                        </span>
                                                    </div>
                                                )}
                                                
                                                {order.isRefunded && (
                                                    <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-green-50 text-green-700 w-fit">
                                                        Refunded
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Order Totals Breakdown - New Format */}
                                    {order.orderTotals && (
                                        <div className="bg-gray-50 p-4 sm:p-6 border-t">
                                            <h3 className="font-medium text-gray-900 mb-3">Order Breakdown</h3>
                                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                                                <div>
                                                    <span className="text-gray-600">Subtotal</span>
                                                    <div className="font-medium">₹{formatCurrency(order.orderTotals.subtotal)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Tax Amount</span>
                                                    <div className="font-medium">₹{formatCurrency(order.orderTotals.totalTaxAmount)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Shipping</span>
                                                    <div className="font-medium">₹{formatCurrency(order.orderTotals.shippingCharges)}</div>
                                                </div>
                                                <div>
                                                    <span className="text-gray-600">Platform Fees</span>
                                                    <div className="font-medium">₹{formatCurrency(order.orderTotals.platformFees)}</div>
                                                </div>
                                                {order.orderTotals.totalDiscount > 0 && (
                                                    <div>
                                                        <span className="text-green-600">Total Discount</span>
                                                        <div className="font-medium text-green-600">-₹{formatCurrency(order.orderTotals.totalDiscount)}</div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {/* Products List */}
                                    <div className="divide-y">
                                        {order.products.map((product, index) => {
                                            const productImage = getProductImage(product);
                                            const productName = getProductName(product);
                                            const productBrand = getProductBrand(product);
                                            const pricing = getProductPricing(product);

                                            return (
                                                <div key={index} className="p-4 sm:p-6">
                                                    <div className="flex flex-col sm:flex-row gap-4">
                                                        {/* Product Image */}
                                                        <div className="w-full sm:w-24 h-24 flex-shrink-0 bg-gray-100 rounded-md overflow-hidden">
                                                            {productImage ? (
                                                                <img
                                                                    src={productImage}
                                                                    alt={productName}
                                                                    className="w-full h-full object-contain"
                                                                />
                                                            ) : (
                                                                <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
                                                                        <circle cx="8.5" cy="8.5" r="1.5"/>
                                                                        <polyline points="21,15 16,10 5,21"/>
                                                                    </svg>
                                                                </div>
                                                            )}
                                                        </div>

                                                        {/* Product Details */}
                                                        <div className="flex-grow">
                                                            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2">
                                                                <div>
                                                                    <h3 className="font-medium text-lg text-gray-900">
                                                                        {productName}
                                                                    </h3>
                                                                    <p className="text-gray-500 text-sm">
                                                                        Brand: {productBrand}
                                                                    </p>
                                                                    
                                                                    {/* Selected Options - New Format */}
                                                                    {product.selectedOptions && (
                                                                        <div className="text-sm text-gray-500 mt-1">
                                                                            {product.selectedOptions.color && (
                                                                                <span>Color: {product.selectedOptions.color}</span>
                                                                            )}
                                                                            {product.selectedOptions.size && (
                                                                                <span className="ml-3">Size: {product.selectedOptions.size}</span>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </div>

                                                                <Link
                                                                    to={`/product/${product.productId || product.product?._id}`}
                                                                    className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                                                                >
                                                                    View Product
                                                                </Link>
                                                            </div>

                                                            <div className="mt-3 grid grid-cols-2 md:grid-cols-4 gap-4">
                                                                <div>
                                                                    <span className="block text-xs text-gray-500">Original Price</span>
                                                                    <span className="font-medium">₹{formatCurrency(pricing.originalPrice)}</span>
                                                                </div>

                                                                {pricing.hasDiscount && (
                                                                    <div>
                                                                        <span className="block text-xs text-gray-500">Paid Price</span>
                                                                        <span className="font-medium text-green-600">₹{formatCurrency(pricing.actualPrice)}</span>
                                                                    </div>
                                                                )}

                                                                <div>
                                                                    <span className="block text-xs text-gray-500">Quantity</span>
                                                                    <span>{product.quantity}</span>
                                                                </div>

                                                                <div>
                                                                    <span className="block text-xs text-gray-500">Item Total</span>
                                                                    <span className="font-medium">₹{formatCurrency(pricing.itemTotal)}</span>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}

                                        {/* Free Products - New Format */}
                                        {order.freeProducts && order.freeProducts.length > 0 && (
                                            <>
                                                <div className="px-4 sm:px-6 py-2 bg-green-50">
                                                    <h4 className="font-medium text-green-800">Free Products</h4>
                                                </div>
                                                {order.freeProducts.map((freeProduct, index) => (
                                                    <div key={`free-${index}`} className="p-4 sm:p-6 bg-green-50">
                                                        <div className="flex items-center gap-4">
                                                            <div className="w-16 h-16 bg-white rounded-md overflow-hidden">
                                                                {freeProduct.productSnapshot.productImages?.[0] ? (
                                                                    <img
                                                                        src={freeProduct.productSnapshot.productImages[0]}
                                                                        alt={freeProduct.productSnapshot.name}
                                                                        className="w-full h-full object-contain"
                                                                    />
                                                                ) : (
                                                                    <div className="w-full h-full flex items-center justify-center text-gray-400">
                                                                        <span className="text-xs">No Image</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                            <div className="flex-grow">
                                                                <h4 className="font-medium text-green-800">
                                                                    {freeProduct.productSnapshot.name}
                                                                </h4>
                                                                <p className="text-green-600 text-sm">
                                                                    Quantity: {freeProduct.quantity} • FREE
                                                                </p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))}
                                            </>
                                        )}
                                    </div>

                                    {/* Order Actions */}
                                    <div className="bg-gray-50 p-4 sm:p-6 flex flex-wrap items-center justify-between gap-3 border-t">
                                        <div className="text-sm text-gray-600">
                                            Order ID: <span className="font-mono">{order._id}</span>
                                        </div>

                                        <div className="flex gap-3">
                                            {(order.invoiceNumber || (order.status >= 2 && order.canDownloadInvoice)) && (
                                                <button 
                                                    onClick={() => downloadInvoice(order._id)} 
                                                    className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition flex items-center gap-2"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                                        <polyline points="7 10 12 15 17 10"></polyline>
                                                        <line x1="12" y1="15" x2="12" y2="3"></line>
                                                    </svg>
                                                    Download Invoice
                                                </button>
                                            )}

                                            {/* Track Order Button - conditionally shown */}
                                            {order.canTrack && (
                                                <button className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition">
                                                    Track Order
                                                </button>
                                            )}

                                            {/* Cancel Order Button - conditionally shown */}
                                            {order.canCancel && (
                                                <button className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 transition">
                                                    Cancel Order
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* Pagination */}
            {pagination && pagination.totalPages > 1 && (
                <div className="flex justify-center items-center mt-8 gap-2">
                    <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={!pagination.hasPrevPage}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Previous
                    </button>
                    
                    <div className="flex items-center gap-2">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                            const pageNum = i + 1;
                            return (
                                <button
                                    key={pageNum}
                                    onClick={() => setCurrentPage(pageNum)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                                        currentPage === pageNum
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                        
                        {pagination.totalPages > 5 && (
                            <>
                                <span className="text-gray-500">...</span>
                                <button
                                    onClick={() => setCurrentPage(pagination.totalPages)}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition ${
                                        currentPage === pagination.totalPages
                                            ? 'bg-blue-600 text-white'
                                            : 'bg-white border border-gray-300 hover:bg-gray-50'
                                    }`}
                                >
                                    {pagination.totalPages}
                                </button>
                            </>
                        )}
                    </div>
                    
                    <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, pagination.totalPages))}
                        disabled={!pagination.hasNextPage}
                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 transition disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Next
                    </button>
                </div>
            )}
        </div>
    );
};

export default Orders;